import { SubmitHandler } from "react-hook-form";
import { Toast } from "@sscs/ui";
import { capitalizeFirstLetter } from "@sscs/utils";
import { apiErrorToast } from "Utils";
import useCurrentOrganizationData from "../../hooks/useCurrentOrganizationData";
import { useInviteCreateUserMutation } from "../../services/invite.api";
import { MembersFormData } from "../../types";
import useInviteForm from "./useInviteForm";

interface Lengthy {
  length: number;
}

const pluralize = (obj: Lengthy): string => {
  return obj.length > 1 ? "s" : "";
};

const useInviteMember = (onDone: () => void) => {
  const currentOrganizationData = useCurrentOrganizationData();
  const [inviteCreateUser] = useInviteCreateUserMutation();
  const form = useInviteForm(currentOrganizationData);

  const { handleSubmit } = form;

  const onSubmit: SubmitHandler<MembersFormData> = async (data) => {
    if (!currentOrganizationData) {
      onDone();
      return;
    }

    let successMessage = "";

    try {
      const { invited, already_exist } = await inviteCreateUser({
        body: {
          emails: data.emails,
          org_groups: data.orgGroups,
          org_role: data.orgRole,
          organization_id: currentOrganizationData.organization_id,
          group_role: data.groupRole,
        },
      }).unwrap();

      successMessage = `${
        invited.length
          ? `Invited ${invited.length} member${pluralize(
              invited,
            )} to the organization`
          : ``
      }${invited.length && already_exist.length ? " and " : ""}${
        already_exist.length
          ? `skipped inviting ${
              already_exist.length
            } existing member${pluralize(already_exist)}`
          : ""
      }`;
      successMessage = capitalizeFirstLetter(successMessage);
    } catch (error) {
      apiErrorToast(
        error,
        `Could not invite member${pluralize(data.emails)} to the organization`,
      );
      return;
    } finally {
      onDone();
    }

    Toast.show({
      title: successMessage,
      type: "success",
    });
  };

  return {
    form,
    groups: currentOrganizationData?.groups || [],
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useInviteMember;
